import React, { useState, useEffect, useRef } from 'react';
import { advertisementService } from '../services/advertisementService';
import '../styles/AdvertisementCarousel.css';

const AdvertisementCarousel = ({ position = 'carousel', autoPlay = true, interval = 5000 }) => {
  const [advertisements, setAdvertisements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef(null);

  useEffect(() => {
    fetchAdvertisements();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [position]);

  useEffect(() => {
    if (autoPlay && advertisements.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % advertisements.length);
      }, interval);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [advertisements, autoPlay, interval]);

  const fetchAdvertisements = async () => {
    setLoading(true);
    const response = await advertisementService.getActiveAdvertisements(position);
    if (response.success && response.advertisements.length > 0) {
      setAdvertisements(response.advertisements);
      // Enregistrer les vues
      response.advertisements.forEach((ad) => {
        if (ad._id) {
          advertisementService.trackView(ad._id);
        }
      });
    }
    setLoading(false);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    if (autoPlay && advertisements.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % advertisements.length);
      }, interval);
    }
  };

  const goToPrevious = () => {
    const newIndex = currentIndex === 0 ? advertisements.length - 1 : currentIndex - 1;
    goToSlide(newIndex);
  };

  const goToNext = () => {
    const newIndex = (currentIndex + 1) % advertisements.length;
    goToSlide(newIndex);
  };

  const handleClick = async (advertisement) => {
    if (advertisement.linkUrl) {
      // Enregistrer le clic
      if (advertisement._id) {
        await advertisementService.trackClick(advertisement._id);
      }
      // Ouvrir le lien
      window.open(advertisement.linkUrl, '_blank', 'noopener,noreferrer');
    }
  };

  if (loading) {
    return (
      <div className="advertisement-carousel loading">
        <div className="carousel-loading">Chargement...</div>
      </div>
    );
  }

  if (advertisements.length === 0) {
    return null;
  }

  return (
    <div className="advertisement-carousel">
      <div className="carousel-container">
        {advertisements.length > 1 && (
          <button
            className="carousel-button carousel-button-prev"
            onClick={goToPrevious}
            aria-label="Image précédente"
          >
            ‹
          </button>
        )}

        <div className="carousel-slides">
          {advertisements.map((ad, index) => (
            <div
              key={ad._id || index}
              className={`carousel-slide ${index === currentIndex ? 'active' : ''}`}
              onClick={() => handleClick(ad)}
              style={{ cursor: ad.linkUrl ? 'pointer' : 'default' }}
            >
              <img
                src={ad.imageUrl}
                alt={ad.title || `Publicité ${index + 1}`}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              {ad.title && (
                <div className="carousel-slide-title">{ad.title}</div>
              )}
            </div>
          ))}
        </div>

        {advertisements.length > 1 && (
          <button
            className="carousel-button carousel-button-next"
            onClick={goToNext}
            aria-label="Image suivante"
          >
            ›
          </button>
        )}
      </div>

      {advertisements.length > 1 && (
        <div className="carousel-indicators">
          {advertisements.map((ad, index) => (
            <button
              key={ad._id || index}
              className={`carousel-indicator ${index === currentIndex ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Aller à l'image ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdvertisementCarousel;





